// The Accounts oversight view's readings (20 Oct): what leadership and viewers look across every
// strategist's book for. Who owns the account, where its deals stand, the pipeline, and who last
// touched it. Read-only: nothing here feeds an action.
//
// Pure and imported by `node --test`.

import type { BookAccount } from '../api/types.ts';
import { dealAmount, openOpportunities, usageFact } from '../sidebar/panelFacts.ts';
import type { StripFact } from '../sidebar/panelFacts.ts';
import { hasSharedSlack, pastOpportunityChip, pastOpportunityDetail, pastOpportunityOf } from '../book/accountFlags.ts';
import { firstName, formatMoney } from '../lib/format.ts';
import { daysFrom, relativeDay } from '../ae/aeFacts.ts';
import { leadDeal } from '../ae/aeSections.ts';

// --- Pipeline ------------------------------------------------------------------------------

// --- Pipeline ------------------------------------------------------------------------------

/** The open deals that carry a figure (the one amount rule, `dealAmount`), each as its amount. */
function dealFigures(row: BookAccount): number[] {
  return openOpportunities(row)
    .map(deal => dealAmount(deal))
    .filter((amount): amount is number => amount != null);
}

/** Every open deal's figure added up (Vikram, 20 Oct); null with none, drawn "—", never "$0". */
export function pipelineTotal(row: BookAccount): number | null {
  const figures = dealFigures(row);
  if (!figures.length) return null;
  return figures.reduce((total, amount) => total + amount, 0);
}

// --- Last activity -------------------------------------------------------------------------

export interface LastActivity {
  day: string;
  // First name of whoever did it: the SDR (or another rep) from the timeline, or the AE.
  who: string;
  // The full name, for the hover.
  who_full: string;
  // 'email', 'call', 'linkedin' for a timeline touch; null for the AE's, which carries none.
  channel: string | null;
  side: 'sdr' | 'ae';
}

/**
 * The newest touch on the account, from the three fields the book carries: the SDR's own last
 * touch (`last_touch`), another rep's (`other_last_touch`), and the AE's (`last_touch_by_ae`). On a
 * tie the SDR's wins, then the other rep's. The channel is read off the timeline entry by that
 * person on that day, when the capped timeline still holds it; the AE's carries none.
 */
export function lastActivity(row: BookAccount): LastActivity | null {
  const candidates: Array<{ day: string | null | undefined; name: string | null; side: 'sdr' | 'ae' }> = [
    { day: row.last_touch, name: row.sdr_owner || null, side: 'sdr' },
    { day: row.other_last_touch, name: row.other_last_author, side: 'sdr' },
    { day: row.last_touch_by_ae, name: row.account_owner, side: 'ae' },
  ];
  let best: { day: string; name: string | null; side: 'sdr' | 'ae' } | null = null;
  for (const candidate of candidates) {
    if (!candidate.day) continue;
    if (!best || candidate.day > best.day) best = { day: candidate.day, name: candidate.name, side: candidate.side };
  }
  if (!best) return null;

  const fullName = best.name || (best.side === 'ae' ? 'AE' : 'Unknown');
  const entry = best.side === 'sdr'
    ? row.timeline.find(touch => touch.day === best!.day && (!best!.name || touch.author === best!.name))
    : undefined;
  return {
    day: best.day, who: firstName(fullName) || fullName, who_full: fullName,
    channel: entry?.channel || null, side: best.side,
  };
}

// --- Sections ------------------------------------------------------------------------------

export const GROUP_OPEN = 0;
export const GROUP_WORKED = 1;
export const GROUP_QUIET = 2;

// How long without a touch, from either side, counts as gone quiet.
export const QUIET_DAYS = 14;

export const OVERSIGHT_GROUP_LABELS: Record<number, string> = {
  [GROUP_OPEN]: 'Open opportunities',
  [GROUP_WORKED]: `Worked in the last ${QUIET_DAYS} days`,
  [GROUP_QUIET]: `Gone quiet (no touch in ${QUIET_DAYS}+ days, or never)`,
};

/** The section an account belongs in: the first that applies. */
export function oversightGroup(row: BookAccount, today: string): number {
  if (openOpportunities(row).length > 0) return GROUP_OPEN;
  const last = lastActivity(row);
  if (last && -daysFrom(last.day, today) < QUIET_DAYS) return GROUP_WORKED;
  return GROUP_QUIET;
}

/** Most recent activity first, never-touched last, then name. Never mutates `rows`. */
export function byRecentActivity(rows: BookAccount[]): BookAccount[] {
  return [...rows].sort((first, second) => {
    const firstDay = lastActivity(first)?.day ?? '';
    const secondDay = lastActivity(second)?.day ?? '';
    if (firstDay !== secondDay) return firstDay < secondDay ? 1 : -1;
    return first.account.localeCompare(second.account);
  });
}

// --- Owners --------------------------------------------------------------------------------

/** Every SDR and AE named on the rows, for the Owner filter, alphabetical. */
export function ownerNames(rows: BookAccount[]): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.sdr_owner) names.add(row.sdr_owner);
    if (row.account_owner) names.add(row.account_owner);
  }
  return [...names].sort((first, second) => first.localeCompare(second));
}

/** Whether the named person owns the account on either side. */
export function ownedBy(row: BookAccount, owner: string): boolean {
  return row.sdr_owner === owner || row.account_owner === owner;
}

// --- Flags ---------------------------------------------------------------------------------

export interface OversightFlag {
  key: 'slack' | 'past' | 'competitor';
  label: string;
  title: string;
}

/** The three flags leadership reads (20 Oct): a shared Slack, a past opportunity, a competitor. */
export function oversightFlags(row: BookAccount): OversightFlag[] {
  const flags: OversightFlag[] = [];
  if (hasSharedSlack(row)) {
    flags.push({ key: 'slack', label: 'Slack', title: 'We share a Slack Connect channel with this company' });
  }
  const past = pastOpportunityOf(row);
  if (past) {
    flags.push({ key: 'past', label: pastOpportunityChip(past), title: 'Past opportunity: ' + pastOpportunityDetail(past) });
  }
  if (row.competitor) {
    flags.push({ key: 'competitor', label: 'Competitor', title: 'Salesforce marks this company a competitor' });
  }
  return flags;
}

// --- The summary line and the panel strip ---------------------------------------------------

export interface OversightCount {
  label: string;
  value: number;
  title: string;
}

/** The counts above the table. The quiet count is exactly the Gone quiet section's rows. */
export function oversightCounts(rows: BookAccount[], today: string): OversightCount[] {
  const withOpenDeal = rows.filter(row => openOpportunities(row).length > 0).length;
  const touchedThisWeek = rows.filter(row => {
    const last = lastActivity(row);
    return Boolean(last) && -daysFrom(last!.day, today) < 7;
  }).length;
  const quiet = rows.filter(row => oversightGroup(row, today) === GROUP_QUIET).length;
  return [
    { label: rows.length === 1 ? 'account' : 'accounts', value: rows.length, title: 'Accounts shown, after search and the owner filter.' },
    { label: 'with an open opportunity', value: withOpenDeal, title: 'At least one open Salesforce opportunity.' },
    { label: 'touched this week', value: touchedThisWeek, title: 'An SDR or AE touch in the last 7 days.' },
    { label: 'gone quiet', value: quiet, title: `The Gone quiet section: no open opportunity, and no touch from either side in ${QUIET_DAYS} days, or never.` },
  ];
}

/** "3d ago · Omair, email", "12d ago · Morgan (AE)", or "Never". */
export function lastActivityText(last: LastActivity | null, today: string): string {
  if (!last) return 'Never';
  const by = last.side === 'ae' ? `${last.who} (AE)` : last.channel ? `${last.who}, ${last.channel}` : last.who;
  return `${relativeDay(last.day, today)} · ${by}`;
}

/** The stage as the lead deal names it, else the account's Salesforce status. */
export function stageText(row: BookAccount): string {
  return leadDeal(row)?.stage || row.status || '—';
}

/** The facts above every panel tab for leadership and viewers (`lib/roleViews.ts` picks it). */
export function oversightFactStrip(row: BookAccount, today: string): StripFact[] {
  const more = Math.max(openOpportunities(row).length - 1, 0);
  const pipeline = pipelineTotal(row);
  const last = lastActivity(row);
  return [
    { label: 'Stage', value: stageText(row), note: more > 0 ? `+${more} more open` : null, tone: null },
    { label: 'Pipeline', value: pipeline == null ? '—' : formatMoney(pipeline)!, note: null, tone: null },
    { label: 'SDR', value: row.sdr_owner || 'Unassigned', note: null, tone: null },
    { label: 'AE', value: row.account_owner || '—', note: null, tone: null },
    { label: 'Last activity', value: lastActivityText(last, today), note: null, tone: null, title: last?.day ?? null },
    usageFact(row),
  ];
}
